import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';

type LanguageToggleProps = {
  variant?: 'nav' | 'floating';
  className?: string;
};

export const LanguageToggle: React.FC<LanguageToggleProps> = ({ variant = 'nav', className = '' }) => {
  const { i18n } = useTranslation();
  const isItalian = i18n.language.startsWith('it');
  const targetLanguage = isItalian ? 'en' : 'it';

  const toggleLanguage = () => {
    i18n.changeLanguage(targetLanguage);
    
    // Force refresh components that might not update with language change
    const event = new Event('languageChanged');
    window.dispatchEvent(event);
  };

  const baseClasses =
    variant === 'floating'
      ? 'group fixed top-20 right-4 z-50 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-md transition-all duration-300 hover:border-gray-300 dark:hover:border-gray-600'
      : 'flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 transition-colors duration-300 hover:border-cyan-500 dark:hover:border-cyan-400 hover:text-cyan-600 dark:text-cyan-400 dark:hover:text-cyan-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:ring-offset-gray-800';

  const iconClasses =
    variant === 'floating'
      ? 'h-6 w-6 text-gray-700 dark:text-gray-300 transition-colors group-hover:text-gray-900 dark:text-gray-100'
      : 'h-4 w-4 text-cyan-600 dark:text-cyan-400 transition-colors';

  return (
    <button
      onClick={toggleLanguage}
      className={`${baseClasses} ${className}`.trim()}
      aria-label={`Switch language to ${targetLanguage === 'en' ? 'English' : 'Italiano'}`}
      title={`Switch to ${targetLanguage === 'en' ? 'English' : 'Italiano'}`}
    >
      <Languages className={iconClasses} />
      {variant === 'floating' ? (
        <span className="absolute right-full top-1/2 mr-2 -translate-y-1/2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-700 dark:text-gray-300 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
          {targetLanguage === 'en' ? 'English' : 'Italiano'}
      </span>
      ) : (
        <span>{isItalian ? 'IT' : 'EN'}</span>
      )}
    </button>
  );
};